import {useFeatureIsOn} from "@growthbook/growthbook-react";
import * as React from "react";
import {useCallback, useEffect, useState} from "react";

import {CLASS_SNAPSHOT_DEMO_DATE_RANGE} from "@khan/activity-sessions-query";
import {
    appendConversationWithErrorMessage,
    langfuseSessionURL,
    useAcknowledgeGuideTerms,
    useSendChatMessage,
    useSendCompletionRequest,
    useSendGuideCommand,
} from "@khan/guide-core";
import {getPromptVariables, setPromptVariable} from "@khan/guide-core-helpers";
import {GuideStateApi, useGuideState} from "@khan/guide-state";
import type {
    ChatInputMetadata,
    HideChatOptions,
    ImageInput,
} from "@khan/guide-types";
import {HIDE_CHAT_NO_MESSAGE} from "@khan/guide-utils";
import {Errors, KAError} from "@khan/logging";
import {useUserAttributes} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

import type {ChatInputComponent} from "./chat-input/chat-input.tsx";
import {GuideConversation} from "./guide-conversation.tsx";

export interface Props {
    conversationRecordedNotice?: React.ReactNode;
    disclaimerNotice?: React.ReactNode;
    expanded?: boolean;
    /**
     * forceHideChatInput allows the caller to forcibly hide the chat input
     */
    forceHideChatOptions?: HideChatOptions;
    showPreferences?: boolean;
    onClosePreferences?: () => unknown;
    skipSpeechOnboarding?: boolean;
    ChatInputComponent?: ChatInputComponent;
}

export const Guide = ({
    conversationRecordedNotice = null,
    disclaimerNotice = null,
    expanded,
    forceHideChatOptions,
    showPreferences,
    onClosePreferences,
    skipSpeechOnboarding,
    ChatInputComponent,
}: Props): React.ReactElement => {
    const {queryParams} = useLocation();
    const attributes = useUserAttributes();
    const hasAccessToAIGuideDev = attributes?.hasAccessToAIGuideDev;
    const acceptGuideAcknowledgement = useAcknowledgeGuideTerms();
    const sendChatMessage = useSendChatMessage();
    const hasAcknowledgedGuide = useGuideState("hasAcknowledgedGuide");
    const sendGuideCommand = useSendGuideCommand();

    const sendCompletionRequest = useSendCompletionRequest();
    const [customPrompt, setCustomPrompt] = useState("");

    // Check if the demo_date_shift flag is enabled
    const [demoStartDate, setDemoStartDate] = useState("");
    const [demoEndDate, setDemoEndDate] = useState("");
    const flag = useFeatureIsOn("demo_date_shift");

    useEffect(() => {
        if (flag) {
            setDemoStartDate(
                CLASS_SNAPSHOT_DEMO_DATE_RANGE.startDate
                    .toISOString()
                    .split("T")[0],
            );
            setDemoEndDate(
                CLASS_SNAPSHOT_DEMO_DATE_RANGE.endDate
                    .toISOString()
                    .split("T")[0],
            );
        }
    }, [flag]);
    const [lastTestCaseId, setLastTestCaseId] = useState("");

    const promptVersionOverride = queryParams["prompt-version-override"];

    const handleAtCommand = useCallback(
        async (text: string) => {
            let messages: Array<string> | null | undefined = null;

            const removeCommand = (txt: string) => {
                // Replace '@command ' with ''
                return txt.replace(txt.split(" ")[0], "").trim();
            };

            // Handle commands
            if (text.startsWith("@he")) {
                messages = [
                    "You can use the following commands.  Note that you can now use just the first few characters of a command as well.",
                    "@prompt\nThis will change the prompt given to the bot\n It is in the form of @prompt prompt",
                    "@showprompt\nThis will list out what the current custom prompt.  Empty prompts will use the default prompt",
                    "@listvariables\nThese are variables that the prompt has access to.",
                    "@jsonvariables\nShow all of the variables and their values as a json.",
                    "@variable\nUse this, followed by the variable name, to output the variable value.",
                    "@setvariable\nUse this, followed by the variable name and value, to change the value for that variable.",
                    "@model\nThis will override the default selection.\n" +
                        "@model base will use the high-capacity model\n" +
                        "@model latest will use the latest available model\n" +
                        "@model model-name will use the named model",
                    "@temperature\nThis will override the default temperature of 0.25.  A temperature of 0.0 will always select the " +
                        "most likely response.  A temperature of 1.0 will select a response at random.",
                    "@completion\n This will allow you to pass the message directly to the completion endpoint.\n" +
                        "Note that this is only a placeholder until a much better solution is built.\n" +
                        "To use it: @completion The Prompt String",
                    "@stream\n This will toggle on/off the response streaming",
                    "@testcase\n This marks the last interaction in this thread as an example of the given test case. To use it: @testcase <test-case-id>. Test case IDs can be created/found at /devadmin/khanmigo/conversation-test-cases.",
                ];
            } else if (text.startsWith("@li")) {
                messages = Object.keys(getPromptVariables());
                if (messages.length === 0) {
                    messages = ["No Variables Are Stored"];
                }
            } else if (text.startsWith("@js")) {
                const jsonVariables = {...getPromptVariables()} as const;
                const contextualLinks = GuideStateApi.latest().contextualLinks;
                if (contextualLinks.length !== 0) {
                    // @ts-expect-error - TS2542 - Index signature in type '{ readonly [x: string]: string; }' only permits reading.
                    jsonVariables["links"] = contextualLinks.join(" \n ");
                }
                messages = [JSON.stringify(jsonVariables, null, 4)];
            } else if (text.startsWith("@va")) {
                const workingString = removeCommand(text);
                const variable = workingString.split(" ")[0].trim();

                const promptVariables = getPromptVariables();
                const key = Object.keys(promptVariables).find((key) => {
                    if (key.toLowerCase() === variable.toLowerCase()) {
                        return true;
                    }
                });

                if (key) {
                    const promptVariable = promptVariables[key];
                    if (promptVariable === "") {
                        messages = ["The value is empty"];
                    } else {
                        messages = [promptVariable];
                    }
                } else {
                    messages = ["Variable not found"];
                }
            } else if (text.startsWith("@se")) {
                const workingString = removeCommand(text);
                // Variable is the first string of characters delimited
                // with a space.
                const variable = workingString.split(" ")[0].trim();
                // Value is everything after the variable name and its
                // terminating delimiter.
                const value = workingString.replace(variable, "").trim();

                setPromptVariable(variable, value);

                messages = [`Variable ${variable} set to ${value}`];
            } else if (text.startsWith("@sh")) {
                messages = [
                    (customPrompt ||
                        GuideStateApi.latest().dynamicBot?.prompt) ??
                        "",
                ];
            } else if (text.startsWith("@pr")) {
                const workingString = removeCommand(text);
                setCustomPrompt(workingString);

                messages = [`Prompt changed to: ${workingString}`];
            } else if (text.startsWith("@mo")) {
                const workingString = removeCommand(text);
                GuideStateApi.set("modelOverride", workingString);

                messages = [`Model changed to: ${workingString}`];
            } else if (text.startsWith("@temp")) {
                const workingString = removeCommand(text);
                const temperature = parseFloat(workingString);
                if (isNaN(temperature)) {
                    messages = ["Temperature must be a number"];
                } else {
                    GuideStateApi.set("temperatureOverride", temperature);
                    messages = [`Temperature changed to: ${temperature}`];
                }
            } else if (text.startsWith("@st")) {
                messages = [
                    `Buffer responses changed to: ${(!GuideStateApi.latest()
                        .bufferResponse).toString()}`,
                ];
                GuideStateApi.set("bufferResponse", (buffer) => !buffer);
            } else if (text.startsWith("@co")) {
                try {
                    const workingString = removeCommand(text);
                    const result = await sendCompletionRequest(
                        workingString,
                        0.3,
                        256,
                        null,
                    );

                    // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ doGeneralAICompletion: DoGeneralAICompletion_doGeneralAICompletion; }>'.
                    if (result.data?.doGeneralAICompletion.error) {
                        throw new KAError(
                            // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ doGeneralAICompletion: DoGeneralAICompletion_doGeneralAICompletion; }>'.
                            result.data.doGeneralAICompletion.error.code,
                            Errors.Internal,
                        );
                    }

                    const message =
                        // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ doGeneralAICompletion: DoGeneralAICompletion_doGeneralAICompletion; }>'.
                        result.data?.doGeneralAICompletion.completion?.text;
                    if (!message) {
                        GuideStateApi.set("conversation", (conversation) =>
                            appendConversationWithErrorMessage(
                                conversation,
                                "No response from completion endpoint",
                            ),
                        );
                        return;
                    }

                    messages = [message];
                } catch (error: any) {
                    GuideStateApi.set("conversation", (conversation) =>
                        appendConversationWithErrorMessage(
                            conversation,
                            "Error calling completion endpoint",
                        ),
                    );
                    return;
                }
            } else if (text.startsWith("@testcase")) {
                const testCaseId = removeCommand(text).trim();

                sendGuideCommand("associate_conversation_test_case", {
                    testCaseId,
                });

                setLastTestCaseId(testCaseId);
            } else if (text.startsWith("@name")) {
                if (lastTestCaseId) {
                    const name = removeCommand(text).trim();

                    sendGuideCommand("set_conversation_test_case_name", {
                        testCaseId: lastTestCaseId,
                        name,
                    });
                } else {
                    messages = [
                        `This command can only be used after using @testcase.`,
                    ];
                }
            } else if (text.startsWith("@success")) {
                if (lastTestCaseId) {
                    sendGuideCommand("set_conversation_test_case_success", {
                        testCaseId: lastTestCaseId,
                        success: "true",
                    });
                } else {
                    messages = [
                        `This command can only be used after using @testcase.`,
                    ];
                }
            } else if (text.startsWith("@fail")) {
                if (lastTestCaseId) {
                    sendGuideCommand("set_conversation_test_case_success", {
                        testCaseId: lastTestCaseId,
                        success: "false",
                    });
                } else {
                    messages = [
                        `This command can only be used after using @testcase.`,
                    ];
                }
            } else if (text.startsWith("@trace")) {
                messages = [];
                const threadId = GuideStateApi.latest().threadId;
                if (threadId) {
                    messages.push(
                        `[View thread traces in Langfuse](${langfuseSessionURL(
                            threadId,
                        )})`,
                    );
                }
            }

            if (messages) {
                const addConversation = messages.map(
                    (message) =>
                        ({
                            type: "incoming",
                            text: message,
                            user: "Teacher",
                            uiOnly: true,
                            serverOnly: false,
                            interactionId: "",
                        }) as const,
                );
                GuideStateApi.set("conversation", (conversation) => [
                    ...conversation,
                    ...addConversation,
                ]);
            }
        },
        [
            customPrompt,
            sendCompletionRequest,
            sendGuideCommand,
            lastTestCaseId,
            setLastTestCaseId,
        ],
    );

    const handleNewUserMessage = useCallback(
        (
            newMessage: string,
            attachedImages: Array<ImageInput>,
            metadata: ChatInputMetadata,
        ) => {
            // Only recognize @ commands for folks with guide dev access.
            if (newMessage[0] === "@" && hasAccessToAIGuideDev) {
                handleAtCommand(newMessage);
            } else {
                sendChatMessage(newMessage, metadata, attachedImages, {
                    customPrompt,
                    promptVersionOverride,
                    demoStartDate,
                    demoEndDate,
                });
            }
        },
        [
            customPrompt,
            handleAtCommand,
            hasAccessToAIGuideDev,
            promptVersionOverride,
            demoStartDate,
            demoEndDate,
            sendChatMessage,
        ],
    );

    if (!hasAcknowledgedGuide) {
        return (
            <GuideConversation
                onMessageSend={acceptGuideAcknowledgement}
                // Allow user to interact with the Guide Acknowledgement flow
                // even if they have an active CD.
                forceHideChatOptions={HIDE_CHAT_NO_MESSAGE}
                conversationRecordedNotice={conversationRecordedNotice}
                disclaimerNotice={disclaimerNotice}
                expanded={expanded}
                ChatInputComponent={ChatInputComponent}
            />
        );
    }

    return (
        <GuideConversation
            conversationRecordedNotice={conversationRecordedNotice}
            disclaimerNotice={disclaimerNotice}
            expanded={expanded}
            forceHideChatOptions={forceHideChatOptions}
            showPreferences={showPreferences}
            onClosePreferences={onClosePreferences}
            onMessageSend={handleNewUserMessage}
            skipSpeechOnboarding={skipSpeechOnboarding}
            ChatInputComponent={ChatInputComponent}
        />
    );
};
